import { useEffect, useRef, type ReactNode } from 'react';
import { m } from 'framer-motion';
import { ArrowUp, Mic, X } from 'lucide-react';
import { cn } from '@/lib/cn';
import { useHaptic } from '@/app/hooks/useHaptic';

/** Кто говорит. Роль приходит из истории сервера и своих сообщений. */
type BubbleRole = 'user' | 'assistant';

export function Bubble({
  role,
  title,
  children,
  wide,
  footer,
  className,
}: {
  role: BubbleRole;
  title?: string;
  children: ReactNode;
  wide?: boolean;
  footer?: ReactNode;
  className?: string;
}) {
  const isUser = role === 'user';
  return (
    <m.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      className={cn(
        // Ответы агента содержат длинные ссылки: без принудительного переноса
        // пузырь становится шире экрана и вся страница едет вбок
        'w-fit border p-3 text-[12.5px] leading-[1.5] shadow-bubble [overflow-wrap:anywhere]',
        wide ? 'max-w-full' : 'max-w-[88%]',
        isUser
          ? 'justify-self-end rounded-[18px_18px_5px_18px] border-brand-deep bg-brand-deep text-white'
          : 'rounded-[18px_18px_18px_5px] border-line/70 bg-surface',
        className,
      )}
    >
      {title ? <strong className="mb-1 block font-medium">{title}</strong> : null}
      <span className="whitespace-pre-line">{children}</span>
      {footer}
    </m.div>
  );
}

/**
 * Лента сообщений. `messages` нужен только чтобы доводить прокрутку до низа при
 * появлении нового сообщения, поэтому тип намеренно широкий: самой ленте
 * содержимое сообщений не нужно.
 */
export function ChatThread({ messages, children }: { messages?: unknown[]; children?: ReactNode }) {
  const endRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' });
  }, [messages?.length]);

  return (
    <div className="mb-3 grid gap-2.5">
      {children}
      <div ref={endRef} />
    </div>
  );
}

export interface ComposerProps {
  value: string;
  onChange: (value: string) => void;
  onSubmit: () => void;
  placeholder?: string;
  disabled?: boolean;
  sending?: boolean;
  /** Дополнительные действия под полем ввода: голос, файл, быстрые правки. */
  actions?: ReactNode;
  label?: string;
  /**
   * Голосовой ответ на той же кнопке, что и текст.
   *
   * Пустое поле — микрофон, набранный текст — отправка, идущая запись — снова
   * отправка. Так же устроены мессенджеры, и отдельная кнопка «остановить и
   * отправить» рядом только путала.
   */
  onVoiceStart?: () => void;
  onVoiceStop?: () => void;
  onVoiceCancel?: () => void;
  recording?: boolean;
}

/** Поле ответа с кнопкой отправки. Растёт по мере ввода, не ломая раскладку WebView. */
export function Composer({
  value,
  onChange,
  onSubmit,
  placeholder,
  disabled,
  sending,
  actions,
  label,
  onVoiceStart,
  onVoiceStop,
  onVoiceCancel,
  recording,
}: ComposerProps) {
  const haptic = useHaptic();
  const ref = useRef<HTMLTextAreaElement>(null);

  useEffect(() => {
    const node = ref.current;
    if (!node) return;
    node.style.height = 'auto';
    node.style.height = `${Math.min(node.scrollHeight, 150)}px`;
  }, [value]);

  const submit = () => {
    if (!value.trim() || disabled || sending) return;
    haptic.impact('medium');
    onSubmit();
  };

  // Голос предлагается только пустому полю: набранный текст отправляется той же
  // кнопкой, иначе одно действие пришлось бы искать в двух местах
  const voiceMode = Boolean(onVoiceStart) && !recording && !value.trim();

  const press = () => {
    if (recording) {
      haptic.impact('medium');
      onVoiceStop?.();
      return;
    }
    if (voiceMode) {
      onVoiceStart?.();
      return;
    }
    submit();
  };

  return (
    <div>
      {label ? <span className="mb-1.5 block text-[12px] font-medium">{label}</span> : null}

      {/*
        Поле занимает всю ширину, а кнопка отправки стоит под ним.
        Рядом с растущим полем она уезжала вниз при каждой новой строке —
        целиться в движущуюся кнопку неудобно.
      */}
      <textarea
        ref={ref}
        value={value}
        disabled={disabled || recording}
        placeholder={recording ? 'Идёт запись…' : placeholder}
        onChange={(event) => onChange(event.target.value)}
        onKeyDown={(event) => {
          if (event.key === 'Enter' && (event.metaKey || event.ctrlKey)) {
            event.preventDefault();
            submit();
          }
        }}
        rows={1}
        className="bd-focus min-h-[46px] w-full resize-none rounded-control border border-line bg-surface px-3 py-3 text-[16px] leading-snug placeholder:text-muted"
      />

      <div className="mt-2 flex items-center gap-2">
        {/*
          Действия в одну строку с прокруткой: при переносе они выстраивались
          столбиком, а кнопка отправки повисала сбоку напротив пустоты
        */}
        <div className="bd-hide-scrollbar flex min-w-0 flex-1 gap-1.5 overflow-x-auto">{actions}</div>

        {recording ? (
          <button
            type="button"
            onClick={onVoiceCancel}
            aria-label="Отменить запись"
            className="bd-focus flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-control border border-line bg-surface text-muted"
          >
            <X className="h-5 w-5" aria-hidden />
          </button>
        ) : null}

        <button
          type="button"
          onClick={press}
          disabled={disabled || sending || (!voiceMode && !recording && !value.trim())}
          aria-label={recording ? 'Отправить запись' : voiceMode ? 'Записать голосом' : 'Отправить'}
          className="bd-focus flex h-[42px] w-[42px] shrink-0 items-center justify-center rounded-control bg-brand text-black transition-opacity disabled:opacity-40"
        >
          {voiceMode ? (
            <Mic className="h-5 w-5" aria-hidden />
          ) : (
            <ArrowUp className={cn('h-5 w-5', sending && 'animate-bd-spin')} aria-hidden />
          )}
        </button>
      </div>
    </div>
  );
}

/** Небольшая кнопка-«таблетка» для быстрых действий под полем ввода. */
export function ChipButton({
  children,
  onClick,
  icon,
  disabled,
}: {
  children: ReactNode;
  onClick?: () => void;
  icon?: ReactNode;
  disabled?: boolean;
}) {
  const haptic = useHaptic();
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={() => {
        haptic.selection();
        onClick?.();
      }}
      className="bd-focus inline-flex shrink-0 items-center gap-1.5 whitespace-nowrap rounded-full border border-line bg-surface/90 px-3 py-2 text-[12px] text-muted transition-colors active:bg-surface-2 disabled:opacity-50"
    >
      {icon}
      {children}
    </button>
  );
}
